import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RefreshCw, AlertTriangle, CheckCircle, Clock } from 'lucide-react';
import Button from '../components/common/Button';
import { checkRestoreToken, restoreAccountByToken, RestoreAccountCheckResponse } from '../services/auth';
import { getErrorMessage } from '../services/api';

export default function RestoreAccount() {
  const { t } = useTranslation('auth');
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [isRestoring, setIsRestoring] = useState(false);
  const [error, setError] = useState('');
  const [accountInfo, setAccountInfo] = useState<RestoreAccountCheckResponse | null>(null);
  const [success, setSuccess] = useState(false);
  const [warnings, setWarnings] = useState<string[]>([]);

  // Verifier le token au chargement
  useEffect(() => {
    async function checkToken() {
      if (!token) {
        setError(t('restoreAccount.invalidToken'));
        setIsLoading(false);
        return;
      }

      try {
        const response = await checkRestoreToken(token);
        if (response.success && response.data) {
          setAccountInfo(response.data);
        } else {
          setError(t('restoreAccount.invalidLinkMessage'));
        }
      } catch (err) {
        setError(getErrorMessage(err));
      } finally {
        setIsLoading(false);
      }
    }

    checkToken();
  }, [token]);

  const handleRestore = async () => {
    setError('');
    setIsRestoring(true);

    try {
      const response = await restoreAccountByToken(token);

      if (response.success) {
        setSuccess(true);
        if (response.data?.warnings) {
          setWarnings(response.data.warnings);
        }
        // Rediriger vers la page de connexion apres 5 secondes
        setTimeout(() => {
          navigate('/login');
        }, 5000);
      }
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsRestoring(false);
    }
  };

  // Etat de chargement
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-12">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <Link to="/" className="text-3xl font-bold text-primary-600">
              Splinity
            </Link>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
            <div className="flex items-center justify-center">
              <RefreshCw className="w-8 h-8 text-primary-600 animate-spin" />
              <span className="ml-3 text-gray-600">{t('restoreAccount.loading')}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Lien invalide ou token manquant
  if (!token || (error && !accountInfo)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-12">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <Link to="/" className="text-3xl font-bold text-primary-600">
              Splinity
            </Link>
            <h1 className="mt-6 text-2xl font-bold text-gray-900">
              {t('restoreAccount.invalidLink')}
            </h1>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-start">
              <AlertTriangle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
              <span>{error || t('restoreAccount.invalidLinkMessage')}</span>
            </div>

            <p className="text-gray-600 text-sm mb-6">
              {t('restoreAccount.tryLogin')}
            </p>

            <Link to="/login">
              <Button type="button" variant="primary" className="w-full">
                {t('restoreAccount.goToLogin')}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Compte restaure avec succes
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-12">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <Link to="/" className="text-3xl font-bold text-primary-600">
              Splinity
            </Link>
            <h1 className="mt-6 text-2xl font-bold text-gray-900">
              {t('restoreAccount.successTitle')}
            </h1>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
            <div className="text-center">
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm flex items-center justify-center">
                <CheckCircle className="w-5 h-5 mr-2" />
                <span>{t('restoreAccount.successMessage')}</span>
              </div>

              {warnings.length > 0 && (
                <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-sm text-left">
                  <p className="font-medium mb-2">{t('restoreAccount.importantNotes')}</p>
                  <ul className="list-disc list-inside space-y-1 text-xs">
                    {warnings.map((warning, index) => (
                      <li key={index}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}

              <p className="text-gray-600 mb-6">
                {t('restoreAccount.welcomeBack')}
              </p>

              <Link to="/login">
                <Button type="button" variant="primary" className="w-full">
                  {t('resetPassword.loginNow')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Affichage principal - confirmation de restauration
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-12">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <Link to="/" className="text-3xl font-bold text-primary-600">
            Splinity
          </Link>
          <h1 className="mt-6 text-2xl font-bold text-gray-900">
            {t('restoreAccount.title')}
          </h1>
          <p className="mt-2 text-gray-600">
            {t('restoreAccount.description')}
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-start">
              <AlertTriangle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {accountInfo && (
            <>
              <div className="mb-6 p-4 bg-primary-50 border border-primary-200 rounded-lg">
                <p className="text-primary-800 font-medium mb-2">
                  {t('restoreAccount.accountToRestore')}
                </p>
                <p className="text-primary-700 text-sm">
                  {accountInfo.firstName && accountInfo.lastName
                    ? `${accountInfo.firstName} ${accountInfo.lastName}`
                    : accountInfo.email}
                </p>
                <p className="text-primary-600 text-xs mt-1">
                  {accountInfo.email}
                </p>
              </div>

              <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start">
                <Clock className="w-5 h-5 text-amber-600 mr-2 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-amber-800 font-medium text-sm">
                    {t('restoreAccount.timeRemaining', { days: accountInfo.daysRemaining })}
                  </p>
                  <p className="text-amber-700 text-xs mt-1">
                    {t('restoreAccount.afterDeadline')}
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <Button
                  type="button"
                  variant="primary"
                  className="w-full"
                  size="lg"
                  isLoading={isRestoring}
                  onClick={handleRestore}
                  disabled={!accountInfo.canRestore}
                >
                  <RefreshCw className="w-5 h-5 mr-2" />
                  {t('restoreAccount.restoreButton')}
                </Button>

                <p className="text-xs text-gray-500 text-center">
                  {t('restoreAccount.restoreDisclaimer')}
                </p>
              </div>
            </>
          )}
        </div>

        <p className="mt-8 text-center text-sm text-gray-500">
          <Link to="/login" className="hover:text-primary-600">
            &larr; {t('restoreAccount.goToLogin')}
          </Link>
        </p>
      </div>
    </div>
  );
}
